import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { EditIcon, type EditIconHandle } from "@/components/ui/edit";
import { TRADE_POSITION_SIDE_BADGE_CLASS, TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { TradeLogGradientBadge } from "@/components/trade-log-gradient-badge";
import { cn } from "@/lib/utils";
import { ExecutionTradeOverlay } from "../../../../../types/trade";
import {
  formatCryptoAmount,
  formatOverlaySlTpAmount,
  simulationSlTpTableShouldShowSim,
  simulationTableValuesDiffer,
} from "../../../../../utils/executionTradeOverlayLevels";
import { useDisplayCurrency } from "../../../../../contexts/DisplayCurrencyContext";
import { useDisplayTimeZone } from "../../../../../contexts/DisplayTimezoneContext";
import {
  COHORT_PREVIEW_LOSS_COLOR,
  COHORT_PREVIEW_WIN_COLOR,
  cohortOutcomeFromOverlay,
} from "../../../../../utils/cohortTradesResolve";
import { formatTradeDateTimeForDisplay } from "../../../../../utils/displayDateTime";
import { pnlBarFill } from "../../../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../tradeLogSortableCell";

const PAGE_SIZE = 25;

const SECONDARY_BADGE_CLASS =
  "rounded-[6px] border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium uppercase shadow-none";

type SortColumn =
  | "symbol"
  | "type"
  | "policy"
  | "simulation"
  | "entryAt"
  | "exitAt"
  | "entryPrice"
  | "exitPrice"
  | "lot"
  | "sl"
  | "tp"
  | "pnl";

function bucketSortRank(bucket?: ExecutionTradeOverlay["bucket_overlay"]): number {
  if (bucket === "in_bucket") return 0;
  if (bucket === "out_of_bucket") return 2;
  return 1;
}

function policySortRank(p: ExecutionTradeOverlay["policy_overlay"]): number {
  if (p === "kept") return 0;
  if (p === "removed") return 2;
  return 1;
}

function simulationSortRank(p: ExecutionTradeOverlay["simulation_overlay"]): number {
  if (p === "changed") return 0;
  if (p === "unchanged") return 1;
  return 2;
}

function compareTradesForSort(
  a: ExecutionTradeOverlay,
  b: ExecutionTradeOverlay,
  col: SortColumn,
  dir: 1 | -1
): number {
  const tie = (a.trade_id || "").localeCompare(b.trade_id || "");
  let c = 0;
  switch (col) {
    case "symbol":
      c = (a.symbol || "").localeCompare(b.symbol || "", undefined, { sensitivity: "base" });
      break;
    case "type":
      c = a.direction.localeCompare(b.direction);
      break;
    case "policy":
      c = policySortRank(a.policy_overlay) - policySortRank(b.policy_overlay);
      break;
    case "simulation":
      c = simulationSortRank(a.simulation_overlay) - simulationSortRank(b.simulation_overlay);
      break;
    case "entryAt": {
      const ta = a.entry_time ? new Date(a.entry_time).getTime() : 0;
      const tb = b.entry_time ? new Date(b.entry_time).getTime() : 0;
      c = ta - tb;
      break;
    }
    case "exitAt": {
      const ta = a.exit_time ? new Date(a.exit_time).getTime() : 0;
      const tb = b.exit_time ? new Date(b.exit_time).getTime() : 0;
      c = ta - tb;
      break;
    }
    case "entryPrice": {
      const pa = a.entry_price != null ? Number(a.entry_price) : Number.NaN;
      const pb = b.entry_price != null ? Number(b.entry_price) : Number.NaN;
      c = (Number.isFinite(pa) ? pa : -Infinity) - (Number.isFinite(pb) ? pb : -Infinity);
      break;
    }
    case "exitPrice": {
      const pa = a.exit_price != null ? Number(a.exit_price) : Number.NaN;
      const pb = b.exit_price != null ? Number(b.exit_price) : Number.NaN;
      c = (Number.isFinite(pa) ? pa : -Infinity) - (Number.isFinite(pb) ? pb : -Infinity);
      break;
    }
    case "lot": {
      const la = a.entry_quantity != null ? Number(a.entry_quantity) : Number.NaN;
      const lb = b.entry_quantity != null ? Number(b.entry_quantity) : Number.NaN;
      const na = Number.isFinite(la) ? la : -Infinity;
      const nb = Number.isFinite(lb) ? lb : -Infinity;
      c = na - nb;
      break;
    }
    case "sl": {
      const sla = a.sl_price != null ? Number(a.sl_price) : Number.NaN;
      const slb = b.sl_price != null ? Number(b.sl_price) : Number.NaN;
      c =
        (Number.isFinite(sla) ? sla : -Infinity) - (Number.isFinite(slb) ? slb : -Infinity);
      break;
    }
    case "tp": {
      const tpa = a.tp_price != null ? Number(a.tp_price) : Number.NaN;
      const tpb = b.tp_price != null ? Number(b.tp_price) : Number.NaN;
      c =
        (Number.isFinite(tpa) ? tpa : -Infinity) - (Number.isFinite(tpb) ? tpb : -Infinity);
      break;
    }
    case "pnl": {
      const pa = a.net_pnl != null ? Number(a.net_pnl) : Number.NaN;
      const pb = b.net_pnl != null ? Number(b.net_pnl) : Number.NaN;
      c = (Number.isFinite(pa) ? pa : -Infinity) - (Number.isFinite(pb) ? pb : -Infinity);
      break;
    }
    default:
      c = 0;
  }
  if (c !== 0) return c * dir;
  return tie * dir;
}

export interface ExecutionTradesListProps {
  trades: ExecutionTradeOverlay[];
  selectedTradeId: string | null;
  scrollToTradeId?: string | null;
  onScrollHandled?: () => void;
  onSelectTrade: (tradeId: string | null) => void;
  onOpenTrade?: (tradeId: string) => void;
  /** @deprecated Pagination replaces capped scroll height. Kept for call-site compatibility. */
  maxHeight?: string;
  showEditColumn?: boolean;
  showPolicyColumn?: boolean;
  showSimulationChangeColumn?: boolean;
  visualStyle?: "default" | "reports";
  /** When true, show a Dust badge on rows with `is_dust` (Include dust & scratch positions). */
  showDustLabels?: boolean;
  /** Market cohort preview: dim out-of-bucket rows and sort in-cohort first. */
  cohortPreviewMode?: boolean;
}

function OpenTradeEditButton({
  tradeId,
  onOpenTrade,
}: {
  tradeId: string;
  onOpenTrade?: (tradeId: string) => void;
}): React.ReactElement {
  const editIconRef = React.useRef<EditIconHandle | null>(null);
  const disabled = !tradeId || !onOpenTrade;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      data-no-row-click=""
      className="size-8 shrink-0"
      aria-label="Open trade for editing"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled || !onOpenTrade) return;
        onOpenTrade(tradeId);
      }}
      onMouseEnter={() => void editIconRef.current?.startAnimation()}
      onMouseLeave={() => void editIconRef.current?.stopAnimation()}
    >
      <EditIcon ref={editIconRef} size={16} className="text-muted-foreground" aria-hidden />
    </Button>
  );
}

const ExecutionTradesList: React.FC<ExecutionTradesListProps> = ({
  trades,
  selectedTradeId,
  scrollToTradeId,
  onScrollHandled,
  onSelectTrade,
  onOpenTrade,
  showEditColumn = true,
  showPolicyColumn = true,
  showSimulationChangeColumn = false,
  visualStyle = "default",
  showDustLabels = false,
  cohortPreviewMode = false,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();
  const isReportsStyle = visualStyle === "reports";
  const rows = useMemo(() => trades || [], [trades]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const sortedRows = useMemo(() => {
    const next = [...rows];
    const sort = sorting[0];
    if (sort?.id) {
      const factor = sort.desc ? -1 : 1;
      next.sort((a, b) => compareTradesForSort(a, b, sort.id as SortColumn, factor));
      return next;
    }
    if (cohortPreviewMode) {
      next.sort((a, b) => {
        const bucketCmp = bucketSortRank(a.bucket_overlay) - bucketSortRank(b.bucket_overlay);
        if (bucketCmp !== 0) return bucketCmp;
        const pa = a.net_pnl != null ? Number(a.net_pnl) : Number.NaN;
        const pb = b.net_pnl != null ? Number(b.net_pnl) : Number.NaN;
        if (!Number.isNaN(pa) && !Number.isNaN(pb) && pa !== pb) return pb - pa;
        return (a.trade_id || "").localeCompare(b.trade_id || "");
      });
    }
    return next;
  }, [rows, sorting, cohortPreviewMode]);

  const pageCount =
    sortedRows.length === 0 ? 0 : Math.ceil(sortedRows.length / pagination.pageSize);

  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [pagination.pageIndex, pageCount]);

  useEffect(() => {
    if (!scrollToTradeId) return;

    const targetId = String(scrollToTradeId).trim().toLowerCase();
    const index = sortedRows.findIndex((t) => {
      const id = String(t.trade_id ?? "").trim().toLowerCase();
      return id.length > 0 && id === targetId;
    });

    if (index < 0) {
      onScrollHandled?.();
      return;
    }

    const pageSize = pagination.pageSize || PAGE_SIZE;
    const nextPage = Math.floor(index / pageSize);
    setPagination((prev) =>
      prev.pageIndex === nextPage ? prev : { ...prev, pageIndex: nextPage }
    );

    // Wait for the page rows to paint, then scroll the highlighted row into view.
    // Do not alter/filter the underlying rows — only navigate to the existing page.
    const frame = window.requestAnimationFrame(() => {
      const row = document.querySelector(
        '[data-trade-review-trades-table] [data-slot="table-row"][data-state="active"]'
      );
      if (row instanceof HTMLElement) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      onScrollHandled?.();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [scrollToTradeId, sortedRows, pagination.pageSize, onScrollHandled]);

  const columns = useMemo((): ColumnDef<ExecutionTradeOverlay>[] => {
    const cols: ColumnDef<ExecutionTradeOverlay>[] = [
      {
        id: "symbol",
        accessorFn: (row) => row.symbol || "",
        meta: { label: "Symbol" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Symbol" />,
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium uppercase">
            {row.original.symbol || "—"}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "type",
        accessorFn: (row) => row.direction,
        meta: { label: "Type" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        cell: ({ row }) => {
          const t = row.original;
          return (
            <TradeLogSortableCell className="flex flex-wrap items-center gap-1">
              <TradePositionSideBadge side={t.direction === "long" ? "buy" : "sell"} />
              {t.close_kind === "partial" ? (
                <TradeLogGradientBadge tone="warning">Partial</TradeLogGradientBadge>
              ) : null}
              {showDustLabels && t.is_dust ? (
                <Badge variant="secondary" className={SECONDARY_BADGE_CLASS}>
                  Dust
                </Badge>
              ) : null}
            </TradeLogSortableCell>
          );
        },
      },
    ];

    if (showPolicyColumn) {
      cols.push({
        id: "policy",
        accessorFn: (row) => row.policy_overlay ?? "",
        meta: { label: "Policy" },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Policy" />,
        cell: ({ row }) => {
          const t = row.original;
          if (t.policy_overlay === "kept") {
            return (
              <TradeLogSortableCell>
                <Badge
                  className={cn(
                    TRADE_POSITION_SIDE_BADGE_CLASS,
                    "bg-[image:linear-gradient(135deg,#38BBD1,#3EEAEC)] uppercase"
                  )}
                >
                  Kept
                </Badge>
              </TradeLogSortableCell>
            );
          }
          if (t.policy_overlay === "removed") {
            const removedOutcome = t.cohort_outcome ?? cohortOutcomeFromOverlay(t);
            const isLoss = removedOutcome === "loss";
            return (
              <TradeLogSortableCell>
                <Badge
                  className={cn(TRADE_POSITION_SIDE_BADGE_CLASS, "uppercase")}
                  style={{
                    backgroundImage: "none",
                    backgroundColor: isLoss
                      ? COHORT_PREVIEW_LOSS_COLOR
                      : COHORT_PREVIEW_WIN_COLOR,
                  }}
                >
                  Removed {isLoss ? "loss" : "win"}
                </Badge>
              </TradeLogSortableCell>
            );
          }
          return (
            <TradeLogSortableCell>—</TradeLogSortableCell>
          );
        },
      });
    }

    if (showSimulationChangeColumn) {
      cols.push({
        id: "simulation",
        accessorFn: (row) => row.simulation_overlay ?? "",
        meta: { label: "Simulation" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Simulation" />
        ),
        cell: ({ row }) => {
          const overlay = row.original.simulation_overlay;
          if (overlay === "changed") {
            return (
              <TradeLogSortableCell>
                <Badge
                  className={cn(
                    TRADE_POSITION_SIDE_BADGE_CLASS,
                    "bg-[image:linear-gradient(135deg,#38BBD1,#3EEAEC)] uppercase"
                  )}
                >
                  Changed
                </Badge>
              </TradeLogSortableCell>
            );
          }
          if (overlay === "unchanged") {
            return (
              <TradeLogSortableCell>
                <Badge
                  className={cn(TRADE_POSITION_SIDE_BADGE_CLASS, "uppercase")}
                  style={{
                    backgroundImage: "none",
                    backgroundColor: "#f49113",
                  }}
                >
                  Unchanged
                </Badge>
              </TradeLogSortableCell>
            );
          }
          return (
            <TradeLogSortableCell>—</TradeLogSortableCell>
          );
        },
      });
    }

    cols.push(
      {
        id: "entryAt",
        accessorFn: (row) => row.entry_time ?? "",
        meta: { label: "Entry Date" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry Date" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell>
            {row.original.entry_time
              ? formatTradeDateTimeForDisplay(row.original.entry_time, displayTimeZone)
              : "—"}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "entryPrice",
        accessorFn: (row) => row.entry_price ?? Number.NEGATIVE_INFINITY,
        meta: { label: "Entry price", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry price" align="right" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell align="right" className="whitespace-nowrap">
            {formatCryptoAmount(row.original.entry_price)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "exitAt",
        accessorFn: (row) => row.exit_time ?? "",
        meta: { label: "Exit Date" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Exit Date" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell>
            {row.original.exit_time
              ? formatTradeDateTimeForDisplay(row.original.exit_time, displayTimeZone)
              : "—"}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "exitPrice",
        accessorFn: (row) => row.exit_price ?? Number.NEGATIVE_INFINITY,
        meta: { label: "Exit price", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Exit price" align="right" />
        ),
        cell: ({ row }) => {
          const t = row.original;
          const showSimExitDelta =
            t.simulation_overlay === "changed" &&
            simulationTableValuesDiffer(t.exit_price, t.sim_exit_price);
          return (
            <TradeLogSortableCell align="right" className="whitespace-nowrap">
              {showSimExitDelta ? (
                <span>
                  {formatCryptoAmount(t.exit_price)}
                  <span className="mx-1">→</span>
                  {formatCryptoAmount(t.sim_exit_price)}
                </span>
              ) : (
                formatCryptoAmount(t.exit_price)
              )}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "lot",
        accessorFn: (row) => row.entry_quantity ?? Number.NEGATIVE_INFINITY,
        meta: { label: "Lot", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Lot" align="right" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell align="right" className="whitespace-nowrap">
            {formatCryptoAmount(row.original.entry_quantity)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "sl",
        accessorFn: (row) => row.sl_price ?? Number.NEGATIVE_INFINITY,
        meta: { label: "SL", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="SL" align="right" />
        ),
        cell: ({ row }) => {
          const t = row.original;
          const showSimSl =
            t.simulation_overlay === "changed" &&
            simulationSlTpTableShouldShowSim(t.sl_price, t.sim_sl_price);
          return (
            <TradeLogSortableCell align="right" className="whitespace-nowrap">
              {showSimSl ? (
                <span>
                  {formatOverlaySlTpAmount(t.sl_price)}
                  <span className="mx-1">→</span>
                  {formatOverlaySlTpAmount(t.sim_sl_price)}
                </span>
              ) : (
                formatOverlaySlTpAmount(t.sl_price)
              )}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "tp",
        accessorFn: (row) => row.tp_price ?? Number.NEGATIVE_INFINITY,
        meta: { label: "TP", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="TP" align="right" />
        ),
        cell: ({ row }) => {
          const t = row.original;
          const showSimTp =
            t.simulation_overlay === "changed" &&
            simulationSlTpTableShouldShowSim(t.tp_price, t.sim_tp_price);
          return (
            <TradeLogSortableCell align="right" className="whitespace-nowrap">
              {showSimTp ? (
                <span>
                  {formatOverlaySlTpAmount(t.tp_price)}
                  <span className="mx-1">→</span>
                  {formatOverlaySlTpAmount(t.sim_tp_price)}
                </span>
              ) : (
                formatOverlaySlTpAmount(t.tp_price)
              )}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "pnl",
        accessorFn: (row) => row.net_pnl ?? Number.NEGATIVE_INFINITY,
        meta: { label: "P&L", align: "end" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="P&L" align="right" />
        ),
        cell: ({ row }) => {
          const t = row.original;
          const pnl = t.net_pnl ?? 0;
          const showSimPnlDelta =
            t.simulation_overlay === "changed" &&
            simulationTableValuesDiffer(t.net_pnl, t.sim_net_pnl);
          const simPnl = t.sim_net_pnl ?? 0;
          return (
            <TradeLogSortableCell align="right" className="whitespace-nowrap">
              {showSimPnlDelta ? (
                <span>
                  <span data-trade-log-pnl="" style={{ color: pnlBarFill(pnl) }}>
                    {formatMoney(pnl)}
                  </span>
                  <span className="mx-1">→</span>
                  <span data-trade-log-pnl="" style={{ color: pnlBarFill(simPnl) }}>
                    {formatMoney(simPnl)}
                  </span>
                </span>
              ) : (
                <span data-trade-log-pnl="" style={{ color: pnlBarFill(pnl) }}>
                  {formatMoney(pnl)}
                </span>
              )}
            </TradeLogSortableCell>
          );
        },
      }
    );

    if (showEditColumn) {
      cols.push({
        id: "edit",
        header: "Edit",
        meta: { label: "Edit", align: "end" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <OpenTradeEditButton
              tradeId={row.original.trade_id || ""}
              onOpenTrade={onOpenTrade}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [
    displayTimeZone,
    formatMoney,
    onOpenTrade,
    showDustLabels,
    showEditColumn,
    showPolicyColumn,
    showSimulationChangeColumn,
  ]);

  const selectedTrade = useMemo(() => {
    if (!selectedTradeId) return null;
    const target = String(selectedTradeId).trim().toLowerCase();
    return (
      rows.find((t) => String(t.trade_id ?? "").trim().toLowerCase() === target) ?? null
    );
  }, [rows, selectedTradeId]);

  const selectedTradeColumns = useMemo((): ColumnDef<ExecutionTradeOverlay>[] => {
    const cols = columns.filter((col) => col.id !== "edit");
    cols.push({
      id: "action",
      header: "Action",
      meta: { label: "Action", align: "end" },
      enableSorting: false,
      enableHiding: false,
      cell: () => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline-red"
            size="sm"
            data-font="primary"
            data-no-row-click=""
            className="h-8 shrink-0 px-3 text-[0.75rem] normal-case tracking-normal"
            onClick={(e) => {
              e.stopPropagation();
              onSelectTrade(null);
            }}
          >
            Clear
          </Button>
        </div>
      ),
    });
    return cols;
  }, [columns, onSelectTrade]);

  return (
    <div
      data-trade-log-trades-table=""
      data-trade-review-trades-table=""
      data-reports-trade-review-table={isReportsStyle ? "" : undefined}
      className="flex w-full flex-col gap-4"
    >
      {selectedTrade ? (
        <div
          data-trade-review-selected-trade=""
          className="flex w-full min-w-0 flex-col gap-2"
        >
          <p className="m-0 font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:text-sidebar-muted-foreground">
            Selected trade
          </p>
          <DataTable
            columns={selectedTradeColumns}
            data={[selectedTrade]}
            getRowId={(row) =>
              row.trade_id || `${row.symbol}-${row.entry_time}-${row.exit_time}-selected`
            }
            activeRowId={selectedTrade.trade_id || undefined}
            getRowDataAttributes={(t) => {
              const attrs: Record<string, string> = {};
              if (cohortPreviewMode && t.bucket_overlay === "out_of_bucket") {
                attrs["data-out-of-bucket"] = "";
              }
              if (t.policy_overlay === "removed") {
                const removedOutcome = t.cohort_outcome ?? cohortOutcomeFromOverlay(t);
                attrs["data-removed-outcome"] =
                  removedOutcome === "loss" ? "loss" : "win";
              }
              if (
                showSimulationChangeColumn &&
                (t.simulation_overlay === "changed" || t.simulation_overlay === "unchanged")
              ) {
                attrs["data-simulation-overlay"] = t.simulation_overlay;
              }
              return attrs;
            }}
            showPagination={false}
            showColumnVisibility={false}
            showTableBorder
            tableClassName="dark:!bg-sidebar"
            emptyMessage="No trades."
          />
        </div>
      ) : null}
      <DataTable
        columns={columns}
        data={sortedRows}
        pageCount={pageCount}
        getRowId={(row) => row.trade_id || `${row.symbol}-${row.entry_time}-${row.exit_time}`}
        onRowClick={(row) => {
          const id = row.trade_id || "";
          if (!id) return;
          onSelectTrade(selectedTradeId === id ? null : id);
        }}
        activeRowId={selectedTradeId ?? undefined}
        getRowDataAttributes={(t) => {
          const attrs: Record<string, string> = {};
          if (cohortPreviewMode && t.bucket_overlay === "out_of_bucket") {
            attrs["data-out-of-bucket"] = "";
          }
          if (t.policy_overlay === "removed") {
            const removedOutcome = t.cohort_outcome ?? cohortOutcomeFromOverlay(t);
            attrs["data-removed-outcome"] =
              removedOutcome === "loss" ? "loss" : "win";
          }
          if (
            showSimulationChangeColumn &&
            (t.simulation_overlay === "changed" || t.simulation_overlay === "unchanged")
          ) {
            attrs["data-simulation-overlay"] = t.simulation_overlay;
          }
          return attrs;
        }}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination
        showColumnVisibility={false}
        showTableBorder
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No trades."
      />
    </div>
  );
};

export default ExecutionTradesList;
